export const BASE_URL = "/api/proposal-outlines";

export const PROGRESS_EVENTS = [
  "started",
  "streaming_and_classifying",
  "generating_outline",
  "parsing_response",
  "saving_results",
];

export const EVENT_TITLES = {
  started: "Starting Generation",
  streaming_and_classifying: "Processing Documents",
  generating_outline: "Generating Outline",
  parsing_response: "Parsing Results",
  saving_results: "Saving Outline",
  type_review_ready: "Confirm Document Types",
  req_review_ready: "Review Requirements",
  completed: "Generation Complete",
  failed: "Generation Failed",
  still_processing: "Still Working",
};

/**
 * Error codes returned by the backend for outline operations.
 * Used for status-code-specific error handling in the frontend.
 */
export const OUTLINE_ERROR_CODES = {
  OUTLINE_EXISTS: "OUTLINE_EXISTS",
  GENERATION_IN_PROGRESS: "GENERATION_IN_PROGRESS",
  NO_CLASSIFIABLE_CONTENT: "NO_CLASSIFIABLE_CONTENT",
  INVALID_DOCUMENTS: "INVALID_DOCUMENTS",
  PROPOSAL_NOT_FOUND: "PROPOSAL_NOT_FOUND",
};

/**
 * SSE stall watchdog: if no event (including heartbeat) arrives within this
 * window while a generation stream is connected, treat the connection as
 * stalled and kick off the reconnect routine.
 */
export const SSE_STALL_TIMEOUT_MS = 90000;

/** Max number of reconnect attempts before giving up and surfacing "lost". */
export const SSE_MAX_RECONNECT_ATTEMPTS = 3;

/** Backoff delay (ms) applied before each reconnect attempt, by attempt index. */
export const SSE_RECONNECT_BACKOFF_MS = [2000, 4000, 8000];

// Refreshes the edit lock while the user is actually editing. Well inside the server's lock TTL,
// so a few missed beats still leave the lock comfortably alive.
export const LOCK_HEARTBEAT_INTERVAL_MS = 300000;

export const PHASE_BY_EVENT = {
  started: "starting",
  streaming_and_classifying: "detecting",
  document_progress: "extracting",
  type_review_ready: "confirmTypes",
  generating_outline: "outlining",
  parsing_response: "outlining",
  req_review_ready: "reviewRequirements",
};

export const OUTLINE_PHASES = [
  "starting",
  "detecting",
  "confirmTypes",
  "extracting",
  "reviewRequirements",
  "outlining",
];

/** Phases where the run pauses for user input before continuing automatically. */
export const GATE_PHASES = ["confirmTypes", "reviewRequirements"];

/** Span the extraction phase creeps across as its requirement batches complete. */
export const EXTRACTION_PROGRESS_START = 30;
export const EXTRACTION_PROGRESS_END = 45;

/**
 * Minimum overall progress implied by each phase. The staged pipeline emits no numeric progress
 * between `started` (0) and `generating_outline` (50), so the ring would otherwise sit at 0 for the
 * whole classify+extract stretch and read as a stalled run. Folded into the slice's monotonic max,
 * so any real server percentage still wins.
 */
export const PHASE_PROGRESS_FLOOR = {
  starting: 0,
  detecting: 15,
  confirmTypes: 25,
  extracting: EXTRACTION_PROGRESS_START,
  reviewRequirements: 45,
  outlining: 50,
};

/**
 * Public name of the feature flag controlling the two user gates (backend:
 * FeatureFlag.OUTLINE_USER_GATES). Disabled by default: the pipeline auto-confirms both gates,
 * never enters the AWAITING_* statuses, and the stepper drops the two gate steps.
 */
export const OUTLINE_USER_GATES_FLAG = "outline-user-gates";

export const STEP_LABELS = {
  starting: "Starting",
  detecting: "Detecting",
  confirmTypes: "Confirm Types",
  extracting: "Extracting",
  reviewRequirements: "Review Requirements",
  outlining: "Outlining",
};

/**
 * Friendly, implementation-free status line shown per phase while generation runs. Used as the
 * default when no more specific backend message applies (see the progress panel); backend
 * messages — generation notes and the periodic "still working" reassurance — override it.
 */
export const PHASE_MESSAGES = {
  starting: "Getting things ready…",
  detecting: "Analyzing your documents to understand their type and structure…",
  extracting: "Reading your documents in full to capture every requirement…",
  outlining: "Organizing everything into a structured outline…",
};

/**
 * Human-readable labels for OutlineDocumentType enum values (mirrors
 * com/visiblethread/proposaloutline/frame/OutlineDocumentType.java). Shared by the completed-view
 * SmartBar and the in-progress DetectedTypeBanner.
 */
export const OUTLINE_TYPE_LABELS = {
  US_FEDERAL_RFP: "US Federal RFP",
  COMMERCIAL_RFP: "Commercial RFP",
  EU_UK_TENDER: "EU / UK Tender",
  GRANT_APPLICATION: "Grant Application",
  HEALTHCARE: "Healthcare",
  BROAD_AGENCY_ANNOUNCEMENT: "Broad Agency Announcement",
  UNKNOWN: "Unknown",
};

/** Shared by the completed-view SmartBar and the in-progress DetectedTypeBanner. */
export const getOutlineTypeLabel = (type) =>
  OUTLINE_TYPE_LABELS[type] || OUTLINE_TYPE_LABELS.UNKNOWN;

/** Shared by the completed-view SmartBar and the in-progress DetectedTypeBanner. */
export const toConfidencePct = (confidence) =>
  Math.round((confidence || 0) * 100);
